import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Spinner, Alert } from 'react-bootstrap';
import { PlusCircle, BookOpen } from 'lucide-react';
import { fetchBooks, deleteBook } from '../api/readerService'; // Adjust path
import { useSelector } from 'react-redux';

const BookListPage = () => {
    const { isAuthenticated, user } = useSelector((state) => state.auth);
    const [books, setBooks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    const loadBooks = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetchBooks();
            setBooks(response.data.results || response.data); // Handle pagination if present
        } catch (err) {
            setError(err.response?.data?.detail || "Failed to fetch books.");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadBooks();
    }, []);

    const handleDeleteBook = async (bookId) => {
        if (window.confirm("Are you sure you want to delete this book and all its chapters?")) {
            try {
                await deleteBook(bookId);
                loadBooks(); // Refresh list
            } catch (err) {
                alert("Failed to delete book: " + (err.response?.data?.detail || err.message));
            }
        }
    };


    if (loading) return <Container className="text-center mt-5"><Spinner animation="border" variant="primary" /><p className="mt-2">Loading books...</p></Container>;
    
    return (
        <Container className="py-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h1>Books</h1>
                {isAuthenticated && user?.is_superuser && (
                <Button variant="success" onClick={() => navigate('/create-book')}>
                    <PlusCircle size={20} className="me-2" />Create New Book
                </Button>)}
            </div>
            {error && <Alert variant="danger">{error}</Alert>}
            {books.length === 0 && !loading && !error && (
                <Alert variant="info">You haven't created any books yet. {isAuthenticated && user?.is_superuser && <Link to="/create-book">Create one now!</Link>}</Alert>
            )}
            <Row xs={1} md={2} lg={3} className="g-4">
                {books.map(book => (
                    <Col key={book.id}>
                        <Card className="h-100 shadow-sm">
                            {book.cover_image_url && 
                                <Card.Img variant="top" src={book.cover_image_url} style={{ height: '200px', objectFit: 'cover' }} alt={book.title} />
                            }
                            {!book.cover_image_url &&
                                <div className="bg-light d-flex align-items-center justify-content-center" style={{ height: '200px'}}>
                                    <BookOpen size={48} className="text-secondary"/>
                                </div>
                            }
                            <Card.Body className="d-flex flex-column">
                                <Card.Title>{book.title}</Card.Title>
                                <Card.Subtitle className="mb-2 text-muted small">
                                    By: {book.author || 'Unknown Author'}
                                </Card.Subtitle>
                                <Card.Text className="text-muted small flex-grow-1">
                                    {book.description ? `${book.description.substring(0, 100)}...` : 'No description.'}
                                    <br/>
                                    Chapters: {book.chapter_count || 0}
                                </Card.Text>
                                <div className="ms-auto mt-auto">
                                    <Button variant="primary" size="sm" as={Link} to={`/book/${book.id}`} className="me-2">
                                        View
                                    </Button>
                                    { isAuthenticated && user?.is_superuser && (
                                    <Button variant="outline-danger" size="sm" onClick={() => handleDeleteBook(book.id)}>
                                        Delete
                                    </Button>)}
                                </div>
                            </Card.Body>
                            <Card.Footer className="text-muted small">
                                Created: {new Date(book.created_at).toLocaleDateString()}
                            </Card.Footer>
                        </Card>
                    </Col>
                ))}
            </Row>
        </Container>
    );
};

export default BookListPage;